import React, { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { Alert, Snackbar } from '@mui/material'

interface MessageHandlerContextType {
  showErrorMessage: (message: string) => void
  showSuccessMessage: (message: string) => void
  clearMessages: () => void
}

interface MessageHandlerProviderProps {
  children: ReactNode
}

const MessageHandlerContext = createContext<MessageHandlerContextType | undefined>(undefined)

const INITIAL_SUCCESS_MESSAGE = 'Request made successfully'
const INITIAL_ERROR_MESSAGE = 'Failed To send your request'

export const MessageHandlerProvider: React.FC<MessageHandlerProviderProps> = ({ children }) => {
  const [errorMessage, setErrorMessage] = useState<string>(INITIAL_ERROR_MESSAGE)
  const [successMessage, setSuccessMessage] = useState<string>(INITIAL_SUCCESS_MESSAGE)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = useState(false)

  const clearMessages = useCallback((): void => {
    setErrorMessage(INITIAL_ERROR_MESSAGE)
    setSuccessMessage(INITIAL_SUCCESS_MESSAGE)
    setShowSuccessAlert(false)
    setShowUnsuccessAlert(false)
  }, [])

  const showErrorMessage = useCallback((message: string): void => {
    clearMessages()
    setErrorMessage(message)
    setShowUnsuccessAlert(true)
  }, [clearMessages])

  const showSuccessMessage = useCallback((message: string): void => {
    clearMessages()
    setSuccessMessage(message)
    setShowSuccessAlert(true)
  }, [clearMessages])

  const handleSnackbarClose = (_event: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return
    }
    clearMessages()
  }

  const value: MessageHandlerContextType = useMemo(() => ({
    showErrorMessage,
    showSuccessMessage,
    clearMessages
  }), [showErrorMessage, showSuccessMessage, clearMessages])

  return (
      <MessageHandlerContext.Provider value={value}>
        {children}
        {showSuccessAlert && (
            <Snackbar open={true} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                {successMessage}
              </Alert>
            </Snackbar>
        )}
        {showUnsuccessAlert && (
            <Snackbar open={true} autoHideDuration={5000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%', whiteSpace: 'pre-line' }}>
                {errorMessage}
              </Alert>
            </Snackbar>
        )}
      </MessageHandlerContext.Provider>
  )
}

export const useGlobalMessageHandler = (): MessageHandlerContextType => {
  const context = useContext(MessageHandlerContext)
  if (context == null) {
    throw new Error('useMessageHandlerContext must be used within a MessageHandlerProvider')
  }
  return context
}
